/**
 * BusinessComplianceNotice — RODO / transparentność dla wizytówek firm
 * z katalogu (import publiczny / Google Places) BEZ przejętego właściciela.
 *
 * Eksportuje:
 *  - BusinessUnclaimedBanner  → górny disclaimer + 3 akcje (Przejmij / Popraw dane / Usuń wizytówkę)
 *  - BusinessDataSourceSection → sekcja "Źródło danych i Twoje prawa" (art. 14 RODO)
 *
 * Renderują się WYŁĄCZNIE gdy business.is_claimed === false (wizytówka nieprzejęta).
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Info } from 'lucide-react';
import { API_BASE as API } from '@/utils/api';
import ClaimReadyHero from '@/components/ClaimReadyHero';

const isUnclaimed = (business) => !!business && business.is_claimed === false;

/* ─────────────── Wspólny modal shell ─────────────── */
function ModalShell({ title, subtitle, accent = '#0ea5e9', onClose, children }) {
  return (
    <div
      data-testid="compliance-modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="compliance-modal"
        style={{
          background: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px',
          padding: '28px', maxWidth: '480px', width: '100%', color: '#e2e8f0',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.3 }}>{title}</h2>
          <button onClick={onClose} aria-label="Zamknij" data-testid="compliance-modal-close"
            style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '2px' }}>
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>
        {subtitle && <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 18px', lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ '--accent': accent }}>{children}</div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '11px 14px', background: '#1e293b', border: '1px solid #334155',
  borderRadius: '8px', color: '#e2e8f0', fontSize: '14px', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '12px', marginBottom: '6px', color: '#cbd5e1' };
const fieldWrap = { marginBottom: '14px' };

function primaryBtn(accent) {
  return {
    flex: 1, padding: '12px 18px', background: accent, color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  };
}
const secondaryBtn = {
  flex: 1, padding: '12px 18px', background: 'transparent', color: '#94a3b8',
  border: '1px solid #334155', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
};

/* ─────────────── Przejmij wizytówkę (claim) ─────────────── */
function ClaimModal({ slug, entityName, onClose }) {
  const [form, setForm] = useState({ email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Podaj email'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/public/business-claim/${slug}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Sprawdź skrzynkę — wysłaliśmy link do edycji!'); }
      else toast.error(data.detail || 'Nie udało się wysłać zgłoszenia');
    } catch { toast.error('Błąd połączenia'); }
    finally { setSending(false); }
  };

  return (
    <ModalShell
      title={done ? 'Sprawdź skrzynkę!' : `Przejmij: ${entityName}`}
      subtitle={done ? null : 'Po weryfikacji emailem otrzymasz pełny dostęp — zdjęcia, opis, dane kontaktowe, a zapytania klientów zaczną trafiać bezpośrednio do Ciebie. Obecnie wszystkie funkcje są darmowe.'}
      accent="#0ea5e9" onClose={onClose}
    >
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '20px' }}>
            Wysłaliśmy link aktywacyjny na <strong style={{ color: '#fff' }}>{form.email}</strong>. Sprawdź też folder <em>Spam/Oferty</em>.
          </p>
          <button onClick={onClose} style={{ ...primaryBtn('#0ea5e9'), width: '100%' }} data-testid="claim-done-close">Zamknij</button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="claim-form">
          <div style={fieldWrap}>
            <label style={labelStyle}>Email *</label>
            <input data-testid="claim-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="twoj@email.pl" style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Telefon (opcjonalnie)</label>
            <input data-testid="claim-phone" type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+48 123 456 789" style={inputStyle} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: '20px' }}>
            <label style={labelStyle}>Wiadomość (opcjonalnie)</label>
            <textarea data-testid="claim-message" rows={3} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Prowadzę tę firmę..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={sending} style={secondaryBtn}>Anuluj</button>
            <button type="submit" disabled={sending} style={primaryBtn('#0ea5e9')} data-testid="claim-submit">
              {sending ? 'Wysyłam...' : 'Przejmij wizytówkę →'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

/* ─────────────── Popraw dane / Zgłoś błąd (report) ─────────────── */
function ReportModal({ slug, entityName, onClose }) {
  const [form, setForm] = useState({ message: '', email: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error('Opisz co należy poprawić'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/business/public/${slug}/report`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'update', message: form.message, email: form.email }),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Dziękujemy — zgłoszenie przyjęte.'); }
      else toast.error(data.detail || 'Nie udało się wysłać');
    } catch { toast.error('Błąd połączenia'); }
    finally { setSending(false); }
  };

  return (
    <ModalShell
      title={done ? 'Zgłoszenie przyjęte' : 'Popraw dane wizytówki'}
      subtitle={done ? null : `Opisz, jakie dane firmy „${entityName}" są nieaktualne lub błędne. Sprawdzimy zgłoszenie i poprawimy wpis.`}
      accent="#f59e0b" onClose={onClose}
    >
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '20px' }}>
            Dziękujemy za zgłoszenie. Zajmiemy się poprawą danych. Najszybciej zaktualizujesz wpis przejmując wizytówkę.
          </p>
          <button onClick={onClose} style={{ ...primaryBtn('#f59e0b'), width: '100%' }} data-testid="report-done-close">Zamknij</button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="report-form">
          <div style={fieldWrap}>
            <label style={labelStyle}>Co należy poprawić? *</label>
            <textarea data-testid="report-message" rows={4} required value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="np. nieaktualny telefon / adres / godziny otwarcia..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: '20px' }}>
            <label style={labelStyle}>Twój email (opcjonalnie — odpiszemy)</label>
            <input data-testid="report-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="twoj@email.pl" style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={sending} style={secondaryBtn}>Anuluj</button>
            <button type="submit" disabled={sending} style={primaryBtn('#f59e0b')} data-testid="report-submit">
              {sending ? 'Wysyłam...' : 'Wyślij zgłoszenie'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

/* ─────────────── Usuń wizytówkę (removal-request) ─────────────── */
function RemoveModal({ slug, entityName, onClose }) {
  const [form, setForm] = useState({ company_name: '', reason: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error('Podaj nazwę firmy'); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error('Podaj email lub telefon do kontaktu'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/business/public/${slug}/removal-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Wizytówka została usunięta z katalogu.'); }
      else toast.error(data.detail || 'Nie udało się usunąć');
    } catch { toast.error('Błąd połączenia'); }
    finally { setSending(false); }
  };

  return (
    <ModalShell
      title={done ? 'Wizytówka usunięta' : 'Usuń tę wizytówkę'}
      subtitle={done ? null : 'Wizytówka zostanie usunięta z katalogu, wyszukiwarki i podglądu publicznego. Potwierdź, że jesteś osobą uprawnioną do reprezentowania firmy.'}
      accent="#ef4444" onClose={onClose}
    >
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '20px' }}>
            Wizytówka <strong style={{ color: '#fff' }}>{entityName}</strong> została usunięta z katalogu i nie będzie już publicznie widoczna. W razie pytań napisz na <a href="mailto:kontakt@apisai.pl" style={{ color: '#38bdf8' }}>kontakt@apisai.pl</a>.
          </p>
          <button onClick={() => { onClose(); window.location.href = '/katalog'; }} style={{ ...primaryBtn('#ef4444'), width: '100%' }} data-testid="remove-done-close">
            Zamknij
          </button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="remove-form">
          <div style={fieldWrap}>
            <label style={labelStyle}>Nazwa firmy *</label>
            <input data-testid="remove-company" required value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder={entityName} style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Email kontaktowy</label>
            <input data-testid="remove-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="twoj@email.pl" style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>lub Telefon</label>
            <input data-testid="remove-phone" type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+48 123 456 789" style={inputStyle} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: '20px' }}>
            <label style={labelStyle}>Powód (opcjonalnie)</label>
            <textarea data-testid="remove-reason" rows={2} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="np. nie chcę być w katalogu" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={sending} style={secondaryBtn}>Anuluj</button>
            <button type="submit" disabled={sending} style={primaryBtn('#ef4444')} data-testid="remove-submit">
              {sending ? 'Usuwam...' : 'Usuń wizytówkę'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

/* ─────────────── GÓRNY BANER (disclaimer + akcje) ─────────────── */
export function BusinessUnclaimedBanner({ slug, entityName, business }) {
  const [modal, setModal] = useState(null); // 'claim' | 'report' | 'remove'
  if (!isUnclaimed(business)) return null;

  return (
    <>
      <ClaimReadyHero
        kind="business"
        entityName={entityName || 'tej firmy'}
        onClaim={() => setModal('claim')}
        onReport={() => setModal('report')}
        onRemove={() => setModal('remove')}
        testids={{ root: 'unclaimed-disclaimer-banner', claim: 'banner-claim-btn', report: 'banner-report-btn', remove: 'banner-remove-btn' }}
      />

      {modal === 'claim' && <ClaimModal slug={slug} entityName={entityName} onClose={() => setModal(null)} />}
      {modal === 'report' && <ReportModal slug={slug} entityName={entityName} onClose={() => setModal(null)} />}
      {modal === 'remove' && <RemoveModal slug={slug} entityName={entityName} onClose={() => setModal(null)} />}
    </>
  );
}

/* ─────────────── SEKCJA "ŹRÓDŁO DANYCH" (art. 14 RODO) ─────────────── */
export function BusinessDataSourceSection({ business }) {
  if (!isUnclaimed(business)) return null;

  let importedStr = '';
  if (business.imported_at) {
    try { importedStr = new Date(business.imported_at).toLocaleDateString('pl-PL'); } catch { importedStr = ''; }
  }

  const rows = [
    { label: 'Źródło danych', value: business.data_source_label || 'Publicznie dostępne dane firmowe' },
    importedStr ? { label: 'Data dodania do katalogu', value: importedStr } : null,
    { label: 'Zakres danych', value: 'Nazwa, adres, telefon, strona www, godziny otwarcia oraz publiczna ocena (jeśli dostępna).' },
    { label: 'Administrator danych', value: 'ApisAI — kontakt@apisai.pl' },
  ].filter(Boolean);

  return (
    <section id="zrodlo-danych" data-testid="data-source-section" style={{ background: 'var(--pb-bg-alt, #0a0a0a)' }}>
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '64px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <Info style={{ width: '18px', height: '18px', color: 'var(--pb-accent, #c9a96e)' }} />
          <h2 style={{ fontFamily: 'var(--pb-serif, serif)', fontSize: '22px', fontWeight: 500, color: 'var(--pb-text, #f5f5f0)', margin: 0 }}>
            Źródło danych i Twoje prawa
          </h2>
        </div>
        <div style={{ border: '1px solid var(--pb-border, #222)', borderRadius: '12px', overflow: 'hidden' }}>
          {rows.map((r, i) => (
            <div key={r.label} data-testid={`data-source-row-${i}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '14px 20px', borderTop: i ? '1px solid var(--pb-border, #222)' : 'none', fontFamily: 'var(--pb-sans, sans-serif)' }}>
              <span style={{ minWidth: '180px', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--pb-text-dim, #888)' }}>{r.label}</span>
              <span style={{ flex: 1, fontSize: '14px', color: 'var(--pb-text-muted, #bbb)' }}>{r.value}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '13px', lineHeight: 1.7, color: 'var(--pb-text-dim, #888)', marginTop: '18px', fontFamily: 'var(--pb-sans, sans-serif)' }}>
          Zgodnie z art. 14 RODO informujemy, że dane tej wizytówki pozyskano ze źródeł publicznie dostępnych. Jeśli reprezentujesz tę firmę, masz prawo do <strong style={{ color: 'var(--pb-text-muted, #bbb)' }}>dostępu, sprostowania, sprzeciwu oraz usunięcia danych</strong>. Aby przejąć, poprawić lub usunąć tę wizytówkę, skorzystaj z przycisków na górze strony lub napisz na{' '}
          <a href="mailto:kontakt@apisai.pl" style={{ color: 'var(--pb-accent, #c9a96e)' }}>kontakt@apisai.pl</a>.{' '}
          Szczegóły w <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--pb-accent, #c9a96e)' }}>Polityce prywatności</a>.
        </p>
      </div>
    </section>
  );
}

export default BusinessUnclaimedBanner;
